import { Component } from "@theme/component";

/**
 * @typedef {object} PersonalisationCanvasRefs
 * @property {HTMLCanvasElement} canvas
 * @property {HTMLElement} configScript
 */

/**
 * Wraps the <canvas> a customer's personalised text is drawn onto. Reads
 * its own configuration (product metafields, resolved server-side by
 * blocks/personalisation-canvas.liquid) from an embedded JSON <script>
 * ref, owns the TrueTypeCanvas renderer, the hidden line-item-property
 * field, and moves itself into the product's media area.
 *
 * Independent of personalisation-input-component — the two communicate
 * only via the `personalisation:input` CustomEvent that component
 * dispatches (see assets/personalisation-input.js).
 *
 * @extends Component<PersonalisationCanvasRefs>
 */
class PersonalisationCanvas extends Component {
  requiredRefs = ["canvas", "configScript"];

  connectedCallback() {
    super.connectedCallback();
    // Lifecycle callbacks can't be async, so the real work happens in #init.
    this.#init();
  }

  async #init() {
    const config = JSON.parse(this.refs.configScript.textContent || "{}");

    // Dynamic import of a Liquid-resolved asset_url — Horizon's
    // "@theme/*" import map only covers entries hand-listed in
    // snippets/scripts.liquid, a core file this kit doesn't edit.
    const { TrueTypeCanvas } = await import(config.rendererUrl);

    const textParamsArray = (config.textParamsArray || []).map((textParams) => ({
      ...textParams,
      xL: textParams.boundary?.xl ?? 0,
      xR: textParams.boundary?.xr ?? 0,
      yT: textParams.boundary?.yt ?? 0,
      yB: textParams.boundary?.yb ?? 0,
    }));

    // ?personalisation_debug=1 draws each text region's boundary (yellow
    // outline, rotation included) directly on the live canvas — the way to
    // confirm rotated xl/xr/yt/yb numbers actually land where Photoshop
    // measurements intended, without a publish/tweak/republish loop.
    const debug = new URLSearchParams(window.location.search).has("personalisation_debug");

    this.renderer = new TrueTypeCanvas(this.refs.canvas, config.image, textParamsArray, debug);
    this.primaryTextInputId = textParamsArray[0]?.textInputId;

    this.refs.canvas.setAttribute("role", "img");
    this.#updateCanvasLabel();

    this.form = document.querySelector('form[data-type="add-to-cart-form"]');
    this.hiddenField = this.form ? this.#ensureHiddenField() : null;

    this.#moveAboveGallery();

    this.handleExternalInput = (event) => this.#handleExternalInput(event);
    document.addEventListener("personalisation:input", this.handleExternalInput);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("personalisation:input", this.handleExternalInput);
  }

  #handleExternalInput(event) {
    const { textInputId, value } = event.detail;
    this.renderer.setValue(textInputId, value);

    if (textInputId === this.primaryTextInputId) {
      if (this.hiddenField) {
        this.hiddenField.value = this.renderer.getFormattedValue(textInputId);
      }
      this.#updateCanvasLabel();
    }
  }

  /** Keeps the canvas's accessible name in sync with what's actually drawn on it. */
  #updateCanvasLabel() {
    if (!this.primaryTextInputId) return;
    const formatted = this.renderer.getFormattedValue(this.primaryTextInputId);
    this.refs.canvas.setAttribute("aria-label", `Product preview personalised with "${formatted}"`);
  }

  /** Finds or creates the hidden line-item-property field on the product form. */
  #ensureHiddenField() {
    const name = "properties[Personalisation]";
    const existing = this.form.querySelector(`[name="${name}"]`);
    if (existing) return existing;

    const field = document.createElement("input");
    field.type = "hidden";
    field.name = name;
    field.value = "";
    this.form.appendChild(field);
    return field;
  }

  /**
   * Moves this component to be the first child of `.product-information__media`,
   * above the real gallery rather than overlaying any part of it. With
   * `media_presentation: grid`, Horizon renders every other product image
   * as its own grid cell wired directly to open the zoom dialog, so
   * nothing here needs to intercept clicks.
   *
   * Note: Horizon always falls back to a carousel on mobile regardless of
   * this setting, so mobile shows this canvas followed by a separate,
   * untouched swipeable carousel of the real photos below it.
   *
   * If `.product-information__media` isn't found (product has no media),
   * this stays wherever it rendered via `{% content_for 'blocks' %}`.
   */
  #moveAboveGallery() {
    const target = document.querySelector(".product-information__media");
    if (target && !target.contains(this)) target.prepend(this);
  }
}

customElements.define("personalisation-canvas-component", PersonalisationCanvas);
